/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { DefaultButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { getScopedTheme } from 'src/core/themes/luisStylist';

type SampleButtonProps = IButtonProps & {
    sampleBody: string;
    sampleTitle: React.ReactNode;
    onRenderBody?: () => React.ReactNode;
};

const theme = getScopedTheme('SampleButton');

const sampleTextStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { textAlign: 'start' } });
const sampleTextWarpStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({
    root: {
        textAlign: 'start',
        overflowWrap: 'anywhere',
        overflow: 'hidden',
        display: '-webkit-box',
        '-webkit-line-clamp': '3',
        '-webkit-box-orient': 'vertical'
    }
});

const getButtonStyles = (checked: boolean, width: number = null, height: number = null) =>
    classNamesFunction<IButtonProps, IButtonStyles>()({
        root: {
            padding: 0,
            overflow: 'hidden',
            width: width ?? 170,
            borderRadius: '5px',
            height: height ?? 'auto',
            boxShadow: theme.boxShadow,
            transition: 'all 100ms ease-in',
            border: checked ? theme.border : 'none',
            '&:hover': { boxShadow: theme.hoverBoxShadow },
            '&:active,&:focus': { border: theme.border }
        }
    });

export const SampleButton = (props: SampleButtonProps) => {
    const { width, height, sampleTitle, onRenderBody, sampleBody, checked, ...rest } = props;
    let sampleBodyToDisplay = sampleBody.slice(0, 60);

    if (sampleBody.length >= 60) {
        sampleBodyToDisplay = sampleBodyToDisplay.concat('...');
    }

    return (
        <DefaultButton
            {...rest}
            styles={mergeStyleSets(getButtonStyles(checked, width ? Number(width) : null, height ? Number(height) : null), rest.styles)}>
            <Stack grow verticalFill horizontalAlign="start" tokens={{ childrenGap: 10, padding: 8 }}>
                <LuisText styles={sampleTextStyles} fontWeight={500}>
                    {sampleTitle}
                </LuisText>

                <LuisText variant="smallPlus" styles={sampleTextWarpStyles} title={sampleBody}>
                    {onRenderBody ? onRenderBody() : sampleBodyToDisplay}
                </LuisText>
            </Stack>
        </DefaultButton>
    );
};
